import { FeatureFlagIds } from "../../../../utils/featureFlagIds";
import { TabName } from "../OrderEditorTabs";
import React, { useState } from "react";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import produce from "immer";
import { updateEditingApp } from "../../../../store/application/actions";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import SearchIcon from '@mui/icons-material/Search';
import CardContent from "@mui/material/CardContent";
import numeral from "numeral";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import { isNil } from "ramda";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from '../../../../store/rootReducer';
import { ApplicationDispatch, SET_LOADING } from "../../../../store/application/types";
import { useBusinessRulesEngine } from "../../../../Data/BusinessRulesData";
import ConfirmationDialog from "../../../Dialogs/ConfirmationDialog";
import { useDialog } from "../../../../providers/DialogProvider";
import TradeInVehicleDialog from "../../../Dialogs/TradeInVehicleDialog/TradeInVehicleDialog";
import { useEmployeeRolePermissions } from "../../../../Data/EmployeeRoleData";
import VehicleEditIconComponent from "../../../VehicleEditIconComponent";
import { useConfiguration } from "../../../../Data/ConfigurationData";
import { JDPowerButton } from "../../../JDPower";
import { getJdPowerDisabledReason } from "../../../JDPower/jdPowerAvailability";
import ErrorDialog from "../../../Dialogs/ErrorDialog";
import { validate } from "uuid";
import styles from "./_vehicle-info-card.module.scss";
import { addNewTradeInVehicle, addNewTradeInVehicle2, updateTradeInVehicle, deleteTradeInVehicle } from "../../../../Data/VehicleData";
import { Constants, permissions } from "../../../../utils/constants";
import { usePostedAppLock } from "../../../../hooks/usePostedAppLock";
import VehicleAddIconComponent from "../../../VehicleAddIconComponent";
import {ApplicationQuoteType, hasValue, LookupItem, PermissionSet, toDollars, TradeInVehicle} from "@amsanalytics/react-shared-library";
import { ILoggedInEmployee } from "../../../../Interfaces/Admin/ILoggedInEmployee";
import { getApplicant } from "../../../../utils/appUtils";
import {userHasPermission} from "../../../../utils/utils";
import { useLeaseQuoteSolve } from "../../../../hooks/UseLeaseQuoteSolve";

interface VehicleInfoCardProps {
  navigate: (tabName: TabName) => void;
  showVehicleComponent: boolean;
}

// Wrapper component to put it in a card to be on the overview screen
export const VehicleInfoCard: React.FC<VehicleInfoCardProps> = (props) => {
  return (
    <Card>
      {props.showVehicleComponent &&
        <VehicleInfoComponent {...props} />
      }
      <TradeInComponent {...props} />
    </Card>
  );
}

interface VehicleInfoComponentProps {
  navigate: (tabName: TabName) => void;
  // classes: Record<"titleText" | "noBorder", string>;
  hideCardHeader?: boolean;
}

export const VehicleInfoComponent: React.FC<VehicleInfoComponentProps> = (props) => {
  const { data: configuration } = useConfiguration();
  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const vehicle = currentApp?.vehicle;
  const activeUser: ILoggedInEmployee = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const organizations = lookups.organizations;
  const vehicleOrganization = organizations.find(o => o.id === vehicle?.organizationId);
  const bookoutSources: LookupItem[] = useSelector((state: AppState) => state.applications.lookups.bookoutSources);

  const vehicleIsInDifferentLocation =
    vehicle?.organizationId
    && currentApp?.organizationId
    && vehicle.organizationId !== currentApp.organizationId;

  const canViewActualCashValueField = userHasPermission(activeUser, permissions.vehicleACV, PermissionSet.R);

  const stockNumberNotIsAGuid = hasValue(vehicle?.stockNumber) && !validate(vehicle?.stockNumber!);

  return (
    <div>
      {!props.hideCardHeader &&
        <div>
          <CardHeader
            avatar={<DriveEtaIcon fontSize="small" />}
            title="Vehicle"
            action={<VehicleButtonPanel {...props} />}
          />
          <Divider variant="middle" />
        </div>
      }
      <CardContent>
        {vehicle ?
          <div className={styles.vehicleCardContainer}>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Year:</div>
              <div>{vehicle.year}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Make:</div>
              <div>{vehicle.make}</div>
            </div>

            <div className={styles.rowItem}>
              <div className={styles.titleText}>Model:</div>
              <div>{vehicle.model}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Price:</div>
              <div>{toDollars(vehicle.carPrice)}</div>
            </div>
            {canViewActualCashValueField && (
              <>
                <div className={styles.rowItem}>
                  <div className={styles.titleText}>ACV:</div>
                  <div>{toDollars(vehicle.acv)}</div>
                </div>
                <div className={styles.rowItem}>
                  <div className={styles.titleText}>Recon:</div>
                  <div>{toDollars(vehicle.recon)}</div>
                </div>
                <div className={styles.rowItem}>
                  <div className={styles.titleText}>Total Cost:</div>
                  <div>{toDollars(vehicle.recon + vehicle.acv)}</div>
                </div>
              </>
            )}

            <div className={styles.rowItem}>
              <div className={styles.titleText}>Bookout:</div>
              <div>{vehicle.bookoutValue ? toDollars(vehicle.bookoutValue) : ''}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Bookout Source:</div>
              <div>
                {vehicle.bookoutSourceId &&
                  vehicle.bookoutSourceId.toLowerCase() !== Constants.bookoutSources.other
                  ? bookoutSources.find(
                    (bs) =>
                      bs.id.toLowerCase() === vehicle.bookoutSourceId?.toLowerCase()
                  )?.name
                  : vehicle.otherBookoutSource}
              </div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Down Payment:</div>
              <div>{toDollars(vehicle.downPayment)}</div>
            </div>

            <div className={styles.rowItem}>
              <div className={styles.titleText}>Miles:</div>
              <div>{hasValue(vehicle.mileage) ? numeral(vehicle.mileage!).format("0,000") : ''}</div>
            </div>

            <div className={styles.rowItem}>
              <div className={styles.titleText}>Type:</div>
              <div>{vehicle.body}</div>
            </div>
            {configuration?.featureFlags?.[FeatureFlagIds.ExcludeStockNumbers] !== true && stockNumberNotIsAGuid && (
              <>
                <div className={styles.rowItem}>
                  <div className={styles.titleText}>Stock #</div>
                  <div>{vehicle.stockNumber}</div>
                </div>
              </>
            )}
            <div className={styles.rowItem}>
              <div className={styles.titleText}>VIN:</div>
              <div>{vehicle.vin}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Location:</div>
              <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                <Tooltip title={vehicleOrganization?.name}>
                  <div style={{ maxWidth: '125px', justifySelf: 'flex-end' }} className={styles.noWrapText}>{vehicleOrganization?.name}</div>
                </Tooltip>
                {vehicleIsInDifferentLocation && (
                  <div>
                    <Tooltip title="This vehicle is in a different location from the application">
                      <InfoIcon fontSize="small" color="error" style={{ marginLeft: '12px' }} />
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          </div>
          : "No vehicle selected"
        }
      </CardContent>
    </div>
  );
}

interface VehicleButtonPanelProps {
  navigate: (tabName: TabName) => void;
  // classes: Record<"titleText" | "noBorder", string>;
}

export const VehicleButtonPanel: React.FC<VehicleButtonPanelProps> = (props) => {
  const businessRulesEngine = useBusinessRulesEngine();
  const { resolveAfterVehicleEdit } = useLeaseQuoteSolve();
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch<ApplicationDispatch>();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const vehicle = currentApp?.vehicle;
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const [isRemoveVehicleDialogOpen, setIsRemoveVehicleDialogOpen] = useState(false);
  const { data: configuration } = useConfiguration();
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const organizations = lookups.organizations;

  const { isSectionLocked, lockedTooltip } = usePostedAppLock();
  const disableEditing = isSectionLocked('Vehicle');
  const vehicleLockTooltip = lockedTooltip('Vehicle');
  const jdPowerDisabledReason = getJdPowerDisabledReason(configuration);

  const openErrorDialog = (errors: string[]) => {
    openDialog({
      children: (
        <ErrorDialog
          title={`Vehicle Errors`}
          errors={errors}
          closeDialog={closeDialog}
        />
      )
    });
  };

  return (
    <div>
      {configuration?.featureFlags?.[FeatureFlagIds.UseJDPower] === true && lookups.jdPowerRegions.length > 0 &&
        <Tooltip title={jdPowerDisabledReason ?? ''} placement="top">
          <span>
            <JDPowerButton
              currentApp={currentApp!}
              lookups={lookups}
              disabled={disableEditing || !!jdPowerDisabledReason}
            />
          </span>
        </Tooltip>
      }
      {activeUser && userHasPermission(activeUser, permissions.vehicles, PermissionSet.C)
        && configuration?.featureFlags?.[FeatureFlagIds.AllowAddVehicleInLOS] === true && currentApp?.organizationId &&
        <Tooltip title={vehicleLockTooltip} placement="top">
          <span>
            <VehicleAddIconComponent
              applicationId={currentApp!.id}
              vehicleOrganizationId={currentApp!.organizationId}
              disabled={disableEditing}
            />
          </span>
        </Tooltip>
      }
      {vehicle &&
        <>
          <Tooltip title={vehicleLockTooltip || "Remove Vehicle"} placement="top">
            <span>
              <IconButton
                size="small"
                disabled={disableEditing}
                onClick={() => {
                  setIsRemoveVehicleDialogOpen(true)
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Dialog open={isRemoveVehicleDialogOpen}
            onClose={() => {
              setIsRemoveVehicleDialogOpen(false)
            }}
          >
            <DialogTitle>Remove vehicle from application?</DialogTitle>
            <DialogActions>
              <Button
                color="inherit"
                onClick={() => setIsRemoveVehicleDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button color="primary" onClick={() => {
                if (currentApp) {
                  const updatedApp = produce(currentApp, currentApp => {
                    currentApp.vehicle = undefined;
                    currentApp.vehicleId = undefined;
                    currentApp.changedVehicle = true;
                    for (const aq of currentApp.applicationQuotes) {
                      aq.useVehiclePrice = false;
                    }
                  })
                  dispatch(updateEditingApp(updatedApp, businessRulesEngine));
                  setIsRemoveVehicleDialogOpen(false)
                }
              }}>
                Remove
              </Button>
            </DialogActions>
          </Dialog>
        </>
      }
      {activeUser && userHasPermission(activeUser, permissions.vehicles, PermissionSet.U) &&
        <Tooltip title={vehicleLockTooltip || "Edit Vehicle"} placement="top">
          <span>
            <VehicleEditIconComponent
              disabled={disableEditing}
              vehicleOnApp={currentApp?.vehicle}
              vehicleToEdit={currentApp?.vehicle}
              organizations={organizations}
              canEditFullVehicle={configuration?.featureFlags?.[FeatureFlagIds.AllowVehicleFullEdit] === true}
              canEditMilage={activeUser && userHasPermission(activeUser, permissions.inventoryMileage, PermissionSet.U)}
              applicationId={currentApp?.id}
              isIndirectApplication={(currentApp?.quoteType === ApplicationQuoteType.Indirect)}
              onVehicleSaved={(savedVehicle) => resolveAfterVehicleEdit(currentApp, savedVehicle)}
            />
          </span>
        </Tooltip>
      }
      <Tooltip title={vehicleLockTooltip || "Search for Vehicle"} placement="top">
        <span>
          <IconButton
            disabled={disableEditing}
            size="small"
            onClick={() => props.navigate("Vehicle")}>
            <SearchIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}

interface TradeInComponentProps {
 
}

export const TradeInComponent: React.FC<TradeInComponentProps> = (props) => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const { data: configuration } = useConfiguration();
  const tradeInVehicle = currentApp?.tradeInVehicle;
  const tradeInVehicle2 = currentApp?.tradeInVehicle2;
  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const businessRulesEngine = useBusinessRulesEngine();
  const [openDialog, closeDialog] = useDialog();
  const dispatch = useDispatch<ApplicationDispatch>();

  const { isSectionLocked, lockedTooltip } = usePostedAppLock();
  const disableEditing = isSectionLocked('Vehicle');
  const vehicleLockTooltip = lockedTooltip('Vehicle');

  // When AllowTradeInWithoutVehicleOfSale is false (default), a vehicle of sale must be attached before adding trade-ins
  const allowTradeInWithoutVehicle = configuration?.featureFlags?.[FeatureFlagIds.AllowTradeInWithoutVehicleOfSale] === true;
  const tradeInBlockedByMissingVehicle = !allowTradeInWithoutVehicle && !currentApp?.vehicle;
  const tradeInDisabledTooltip = tradeInBlockedByMissingVehicle ? "Attach a vehicle of sale first" : undefined;

  const removeTradeIn = () => {
    openDialog({
      children: (
        <ConfirmationDialog
          closeDialog={closeDialog}
          prompt="Are you sure you want to delete this trade vehicle?"
          title="Confirm Trade Vehicle Removal"
          accept={async () => {
            const tradeInId = currentApp!.tradeInVehicle!.id;

            let deleteResult: Awaited<ReturnType<typeof deleteTradeInVehicle>> | undefined;
            if (hasValue(tradeInId)) {
              deleteResult = await deleteTradeInVehicle(dispatch, tradeInId!, currentApp!.id);
              if (!deleteResult) {
                closeDialog();
                return;
              }
            }

            const updatedApp = produce(currentApp, currentApp => {
              // Promote surviving Trade-In 2 into slot 1 to match the
              // server-side promotion that already ran in
              // DeleteTradeInVehicleAsync. Promoting both the objects and
              // the IDs keeps every downstream consumer consistent:
              // FundingPage seeds from the IDs, quoters detect slots from
              // the objects, and VehicleInfoCard renders from both.
              const survivor = currentApp!.tradeInVehicle2;
              currentApp!.tradeInVehicle = survivor;
              currentApp!.tradeInVehicleId = currentApp!.tradeInVehicle2Id;
              currentApp!.tradeInVehicle2 = undefined;
              currentApp!.tradeInVehicle2Id = undefined;

              if (currentApp!.applicationFunding) {
                currentApp!.applicationFunding.tradeInVehicleId = currentApp!.tradeInVehicleId;
                currentApp!.applicationFunding.tradeInVehicle2Id = undefined;
              }

              // Recompute quote totals from remaining active trade-in only
              const remainingPayoff = survivor?.deletedDate ? 0 : (survivor?.tradeBalance ?? 0);
              const remainingValue = survivor?.deletedDate ? 0 : (survivor?.tradeValue ?? 0);
              currentApp!.financeQuotes.forEach(financeQuote => {
                financeQuote.tradeInPayoff = remainingPayoff;
                financeQuote.tradeInValue = remainingValue;
              });
              if (currentApp!.applicationLeaseQuote) {
                currentApp!.applicationLeaseQuote.tradeAllowance = remainingValue;
              }

              // Carry the refreshed concurrency stamp through the
              // updateEditingApp dispatch so the next save's staleness
              // check compares against the post-mutation server value.
              if (deleteResult?.applicationModifiedDate) {
                currentApp!.modifiedDate = new Date(deleteResult.applicationModifiedDate);
              }
            });
            dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
            closeDialog();
          }}
        />
      )
    });
  };

  const removeTradeIn2 = () => {
    openDialog({
      children: (
        <ConfirmationDialog
          closeDialog={closeDialog}
          prompt="Are you sure you want to delete this trade vehicle?"
          title="Confirm Trade Vehicle 2 Removal"
          accept={async () => {
            const tradeIn2Id = currentApp!.tradeInVehicle2!.id;

            let deleteResult: Awaited<ReturnType<typeof deleteTradeInVehicle>> | undefined;
            if (hasValue(tradeIn2Id)) {
              deleteResult = await deleteTradeInVehicle(dispatch, tradeIn2Id!, currentApp!.id);
              if (!deleteResult) {
                closeDialog();
                return;
              }
            }

            const updatedApp = produce(currentApp, currentApp => {
              currentApp!.tradeInVehicle2 = undefined;
              currentApp!.tradeInVehicle2Id = undefined;
              if (currentApp!.applicationFunding) {
                currentApp!.applicationFunding.tradeInVehicle2Id = undefined;
                // Clear slot 1 if it was promoted from the now-deleted Trade-In 2
                if (currentApp!.applicationFunding.tradeInVehicleId === tradeIn2Id) {
                  currentApp!.applicationFunding.tradeInVehicleId = undefined;
                }
              }

              // Recompute quote totals from remaining active trade-in only
              const remainingPayoff = currentApp!.tradeInVehicle?.deletedDate ? 0 : (currentApp!.tradeInVehicle?.tradeBalance ?? 0);
              const remainingValue = currentApp!.tradeInVehicle?.deletedDate ? 0 : (currentApp!.tradeInVehicle?.tradeValue ?? 0);
              currentApp!.financeQuotes.forEach(financeQuote => {
                financeQuote.tradeInPayoff = remainingPayoff;
                financeQuote.tradeInValue = remainingValue;
              });
              if (currentApp!.applicationLeaseQuote) {
                currentApp!.applicationLeaseQuote.tradeAllowance = remainingValue;
              }

              if (deleteResult?.applicationModifiedDate) {
                currentApp!.modifiedDate = new Date(deleteResult.applicationModifiedDate);
              }
            });
            dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
            closeDialog();
          }}
        />
      )
    });
  };

  const editTradeIn = (vehicle?: TradeInVehicle) => {
    openDialog({
      children: (
        <TradeInVehicleDialog
          vehicle={vehicle}
          accept={async (tradeInVehicle: TradeInVehicle) => {
            closeDialog();
            dispatch({ type: SET_LOADING, payload: true });

            try {
              let newTradeInVehicle: TradeInVehicle | undefined = undefined;
              let applicationModifiedDate: string | null | undefined;
              let newAppStatusId: string | undefined;

              if (!hasValue(currentApp?.tradeInVehicle?.id) || !!currentApp?.tradeInVehicle?.deletedDate) {
                const addResult = await addNewTradeInVehicle(dispatch, tradeInVehicle, currentApp!.id);
                if (!addResult) return;
                newTradeInVehicle = addResult.tradeInVehicle;
                applicationModifiedDate = addResult.applicationModifiedDate;
                newAppStatusId = addResult.newAppStatusId;
              } else {
                const updateResult = await updateTradeInVehicle(dispatch, tradeInVehicle);
                if (!updateResult) return;
                applicationModifiedDate = updateResult.applicationModifiedDate;
                newAppStatusId = updateResult.newAppStatusId;
              }

              const resolvedTradeIn1 = newTradeInVehicle ?? tradeInVehicle;
              const updatedApp = produce(currentApp!, newCurrentApp => {
                newCurrentApp.tradeInVehicle = resolvedTradeIn1;
                newCurrentApp.tradeInVehicleId = resolvedTradeIn1.id;
                if (newCurrentApp.applicationFunding) {
                  newCurrentApp.applicationFunding.tradeInVehicleId = resolvedTradeIn1.id;
                  // Restore TI2 funding FK if a surviving trade-in 2 exists but
                  // lost its funding slot during the earlier TI1 delete + promotion
                  const ti2 = newCurrentApp.tradeInVehicle2;
                  if (ti2 && !ti2.deletedDate && !newCurrentApp.applicationFunding.tradeInVehicle2Id) {
                    newCurrentApp.applicationFunding.tradeInVehicle2Id = ti2.id;
                  }
                }

                // Recompute quote totals from both trade-ins combined
                const ti2Deleted = !!newCurrentApp.tradeInVehicle2?.deletedDate;
                const combinedPayoff = (tradeInVehicle.tradeBalance ?? 0) + (ti2Deleted ? 0 : (newCurrentApp.tradeInVehicle2?.tradeBalance ?? 0));
                const combinedValue = (tradeInVehicle.tradeValue ?? 0) + (ti2Deleted ? 0 : (newCurrentApp.tradeInVehicle2?.tradeValue ?? 0));
                newCurrentApp.financeQuotes.forEach(financeQuote => {
                  financeQuote.tradeInPayoff = combinedPayoff;
                  financeQuote.tradeInValue = combinedValue;
                });

                if (newCurrentApp.applicationLeaseQuote) {
                  newCurrentApp.applicationLeaseQuote.tradeAllowance = combinedValue;
                }

                if (applicationModifiedDate) {
                  newCurrentApp.modifiedDate = new Date(applicationModifiedDate);
                }

                if (hasValue(newAppStatusId)) {
                  newCurrentApp.appStatusId = newAppStatusId!;
                }
              });
              dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
            } finally {
              dispatch({ type: SET_LOADING, payload: false });
            }
          }}
          vehicleOfSaleStockNumber={currentApp?.vehicle?.stockNumber}
          defaultBalance={primary?.customer?.customerIncomeDebt[0]?.priorVehicleAmountBorrowed}
          closeDialog={closeDialog}
        />
      )
    })
  };

  const editTradeIn2 = (vehicle?: TradeInVehicle) => {
    openDialog({
      children: (
        <TradeInVehicleDialog
          vehicle={vehicle}
          tradeInNumber={2}
          accept={async (tradeInVehicle2: TradeInVehicle) => {
            let newTradeInVehicle2: TradeInVehicle | undefined = undefined;
            let applicationModifiedDate: string | null | undefined;
            let newAppStatusId: string | undefined;

            if (!hasValue(currentApp?.tradeInVehicle2?.id) || !!currentApp?.tradeInVehicle2?.deletedDate) {
              const addResult = await addNewTradeInVehicle2(dispatch, tradeInVehicle2, currentApp!.id);
              if (!addResult) return; // API call failed — don't persist a ghost trade-in
              newTradeInVehicle2 = addResult.tradeInVehicle;
              applicationModifiedDate = addResult.applicationModifiedDate;
              newAppStatusId = addResult.newAppStatusId;
            } else {
              const updateResult = await updateTradeInVehicle(dispatch, tradeInVehicle2);
              if (!updateResult) return;
              applicationModifiedDate = updateResult.applicationModifiedDate;
              newAppStatusId = updateResult.newAppStatusId;
            }

            const resolvedTradeIn2 = newTradeInVehicle2 ?? tradeInVehicle2;
            const updatedApp = produce(currentApp!, newCurrentApp => {
              newCurrentApp.tradeInVehicle2 = resolvedTradeIn2;
              newCurrentApp.tradeInVehicle2Id = resolvedTradeIn2.id;
              const isTradeIn1Deleted = !!newCurrentApp.tradeInVehicle?.deletedDate;
              if (newCurrentApp.applicationFunding) {
                // Preserve the slot promotion done by removeTradeIn
                if (isTradeIn1Deleted) {
                  newCurrentApp.applicationFunding.tradeInVehicleId = resolvedTradeIn2.id;
                } else {
                  newCurrentApp.applicationFunding.tradeInVehicle2Id = resolvedTradeIn2.id;
                }
              }

              const tradeIn1Payoff = isTradeIn1Deleted ? 0 : (newCurrentApp.tradeInVehicle?.tradeBalance ?? 0);
              const tradeIn1Value = isTradeIn1Deleted ? 0 : (newCurrentApp.tradeInVehicle?.tradeValue ?? 0);
              const combinedPayoff = tradeIn1Payoff + (resolvedTradeIn2.tradeBalance ?? 0);
              const combinedValue = tradeIn1Value + (resolvedTradeIn2.tradeValue ?? 0);
              newCurrentApp.financeQuotes.forEach(financeQuote => {
                financeQuote.tradeInPayoff = combinedPayoff;
                financeQuote.tradeInValue = combinedValue;
              });

              if (newCurrentApp.applicationLeaseQuote) {
                newCurrentApp.applicationLeaseQuote.tradeAllowance = combinedValue;
              }

              if (applicationModifiedDate) {
                newCurrentApp.modifiedDate = new Date(applicationModifiedDate);
              }

              if (hasValue(newAppStatusId)) {
                newCurrentApp.appStatusId = newAppStatusId!;
              }
            });
            dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
            closeDialog();
          }}
          vehicleOfSaleStockNumber={currentApp?.vehicle?.stockNumber}
          closeDialog={closeDialog}
        />
      )
    })
  };

  const tradeIn1Exists = !isNil(tradeInVehicle) && !hasValue(tradeInVehicle.deletedDate);
  const tradeIn2Exists = !isNil(tradeInVehicle2) && !hasValue(tradeInVehicle2.deletedDate);

  return (
    <div>
      <CardHeader
        avatar={<DriveEtaIcon fontSize="small" />}
        title="Trade-In 1 Vehicle"
        action={
          <>
            {tradeIn1Exists &&
              <Tooltip title={vehicleLockTooltip || "Delete Trade-In"} placement="top">
                <span>
                  <IconButton size="small" style={{ marginRight: 10 }} disabled={disableEditing} onClick={removeTradeIn}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            }
            <Tooltip title={vehicleLockTooltip || tradeInDisabledTooltip || (tradeIn1Exists ? "Edit Trade-In" : "Add Trade-In")} placement="top">
              <span>
                <IconButton size="small"
                  disabled={disableEditing || (!tradeIn1Exists && tradeInBlockedByMissingVehicle)}
                  onClick={() => editTradeIn(tradeIn1Exists ? tradeInVehicle : undefined)}>
                  {tradeIn1Exists
                    ? <EditIcon fontSize="small" />
                    : <AddIcon fontSize="small" />
                  }
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      />
      <Divider variant="middle" />
      <CardContent>
        {tradeIn1Exists
          ?
          <div className={styles.vehicleCardContainer}>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Year:</div>
              <div>{tradeInVehicle.year}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Make:</div>
              <div>{tradeInVehicle.make}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Model:</div>
              <div>{tradeInVehicle.model}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Price:</div>
              <div>{toDollars(tradeInVehicle.tradeValue)}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>ACV:</div>
              <div>{toDollars(tradeInVehicle.acv)}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Payoff:</div>
              <div>{toDollars(tradeInVehicle.tradeBalance)}</div>
            </div>
            {tradeInVehicle.lienHolderName &&
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Lien Holder:</div>
                <div>{tradeInVehicle.lienHolderName}</div>
              </div>
            }
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Miles:</div>
              <div>{tradeInVehicle.mileage ? numeral(tradeInVehicle.mileage).format("0,000") : ''}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>Stock #:</div>
              <div>{tradeInVehicle.stockNumber}</div>
            </div>
            <div className={styles.rowItem}>
              <div className={styles.titleText}>VIN:</div>
              <div>{tradeInVehicle.vin}</div>
            </div>
          </div>
          :
          "No trade in"
        }
      </CardContent>

      {/* Add Trade-In 2 button - shown when Trade-In 1 exists but Trade-In 2 does not */}
      {tradeIn1Exists && !tradeIn2Exists &&
        <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 8 }}>
          <Tooltip title={vehicleLockTooltip || tradeInDisabledTooltip || "Add Trade-In 2"} placement="top">
            <span>
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={disableEditing || tradeInBlockedByMissingVehicle}
                onClick={() => editTradeIn2(undefined)}
              >
                Add Trade-In 2
              </Button>
            </span>
          </Tooltip>
        </div>
      }

      {/* Trade-In 2 section */}
      {tradeIn2Exists &&
        <>
          <CardHeader
            avatar={<DriveEtaIcon fontSize="small" />}
            title="Trade-In 2 Vehicle"
            action={
              <>
                <Tooltip title={vehicleLockTooltip || "Delete Trade-In 2"} placement="top">
                  <span>
                    <IconButton size="small" style={{ marginRight: 10 }} disabled={disableEditing} onClick={removeTradeIn2}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={vehicleLockTooltip || "Edit Trade-In 2"} placement="top">
                  <span>
                    <IconButton size="small"
                      disabled={disableEditing}
                      onClick={() => editTradeIn2(tradeInVehicle2)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            }
          />
          <Divider variant="middle" />
          <CardContent>
            <div className={styles.vehicleCardContainer}>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Year:</div>
                <div>{tradeInVehicle2.year}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Make:</div>
                <div>{tradeInVehicle2.make}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Model:</div>
                <div>{tradeInVehicle2.model}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Price:</div>
                <div>{toDollars(tradeInVehicle2.tradeValue)}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>ACV:</div>
                <div>{toDollars(tradeInVehicle2.acv)}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Payoff:</div>
                <div>{toDollars(tradeInVehicle2.tradeBalance)}</div>
              </div>
              {tradeInVehicle2.lienHolderName &&
                <div className={styles.rowItem}>
                  <div className={styles.titleText}>Lien Holder:</div>
                  <div>{tradeInVehicle2.lienHolderName}</div>
                </div>
              }
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Miles:</div>
                <div>{tradeInVehicle2.mileage ? numeral(tradeInVehicle2.mileage).format("0,000") : ''}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>Stock #:</div>
                <div>{tradeInVehicle2.stockNumber}</div>
              </div>
              <div className={styles.rowItem}>
                <div className={styles.titleText}>VIN:</div>
                <div>{tradeInVehicle2.vin}</div>
              </div>
            </div>
          </CardContent>
        </>
      }
    </div>
  );
}
